import { useCallback } from 'react';
import isEmptyFn from 'lodash/isEmpty';

import ErrorState from '@/components/Error';
import Error403 from '@/components/Error/403';
import Loader from '@/components/Loader';
import EmptyState from '@/modules/corporate/components/SearchListEmptyState';

import { AsyncViewProps } from './types';

const AsyncView = <T,>(props: AsyncViewProps<T>) => {
  const {
    fetching,
    error,
    data,
    isEmpty,
    isAccessDenied,
    operation,
    filterManager,
    searchRef,
    renderLoader,
    LoaderProps,
    renderError,
    ErrorProps,
    renderEmptyState,
    EmptyStateProps,
    renderAccessDenied,
    AccessDeniedProps,
    renderData,
  } = props;

  const onClearSearch = useCallback(() => {
    filterManager?.methods.clearSearch();
    searchRef?.current?.clear();
  }, [filterManager?.methods, searchRef]);

  if (fetching) {
    return renderLoader ? renderLoader() : <Loader {...LoaderProps} />;
  }

  if (isAccessDenied) {
    return renderAccessDenied ? renderAccessDenied() : <Error403 {...AccessDeniedProps} />;
  }

  if (error) {
    return renderError ? (
      renderError(error)
    ) : (
      <ErrorState error={error} operation={operation} {...ErrorProps} />
    );
  }

  if (isEmpty ?? isEmptyFn(data)) {
    return renderEmptyState ? (
      renderEmptyState()
    ) : (
      <EmptyState
        clearFilters={filterManager?.methods.clearFilters}
        clearSearch={onClearSearch}
        filter={filterManager?.state.filter}
        search={filterManager?.state.search}
        {...EmptyStateProps}
      />
    );
  }

  return renderData(data as NonNullable<T>);
};

export default AsyncView;
